import { X, MapPin, CalendarClock, ExternalLink, ShieldCheck, Users } from 'lucide-react';
import type { WingEvent } from '@wingman/shared';

interface EventDetailsProps {
  event: WingEvent;
  onClose: () => void;
}

const CATEGORY_LABELS: Record<string, string> = {
  nightlife: 'Nightlife',
  music: 'Music',
  'food-drink': 'Food & Drink',
  'arts-theatre': 'Arts & Theatre',
  'sports-fitness': 'Sports & Fitness',
  'outdoors-parks': 'Outdoors & Parks',
  'family-kids': 'Family & Kids',
  'learning-classes': 'Learning & Classes',
  'community-social': 'Community & Social',
  'volunteer-civic': 'Volunteer & Civic',
  'markets-shopping': 'Markets & Shopping',
  'film-media': 'Film & Media',
  'games-play': 'Games & Play',
  wellness: 'Wellness',
  experiences: 'Experiences',
  lgbtq: 'LGBTQ+',
};

const CATEGORY_COLORS: Record<string, string> = {
  nightlife: '#e040fb',
  music: '#ff6d00',
  'food-drink': '#00e676',
  'arts-theatre': '#ffea00',
  'sports-fitness': '#00b0ff',
  'outdoors-parks': '#69f0ae',
  'family-kids': '#ffab40',
  'learning-classes': '#18ffff',
  'community-social': '#ff4081',
  'volunteer-civic': '#f48fb1',
  'markets-shopping': '#b388ff',
  'film-media': '#8c9eff',
  'games-play': '#b2ff59',
  wellness: '#76ff03',
  experiences: '#7c4dff',
  lgbtq: '#ea80fc',
};

const SOURCE_LABELS: Record<string, string> = {
  eventbrite: 'Eventbrite',
  seatgeek: 'SeatGeek',
  ticketmaster: 'Ticketmaster',
  nyc_permitted: 'NYC Permitted Events',
  nyc_gov: 'NYC.gov',
  resident_advisor: 'Resident Advisor',
};

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function EventDetails({ event, onClose }: EventDetailsProps) {
  const color = CATEGORY_COLORS[event.categorySlug] || '#00f5ff';
  const label = CATEGORY_LABELS[event.categorySlug] || event.categorySlug;
  const verified = event.trustLevel === 'api_verified' || event.trustLevel === 'venue_official';
  const sourceLabel = SOURCE_LABELS[event.source] || event.source;

  return (
    <div
      style={{
        position: 'absolute',
        top: 'var(--topbar-h)',
        left: 0,
        width: 380,
        maxWidth: '100vw',
        height: 'calc(100% - var(--topbar-h))',
        zIndex: 20,
        boxShadow: '4px 0 24px rgba(0,0,0,0.5)',
        overflowY: 'auto',
        background: 'var(--panel)',
      }}
    >
      {/* Header */}
      <div
        style={{
          width: '100%',
          minHeight: 120,
          flexShrink: 0,
          background: `linear-gradient(135deg, ${color}18 0%, var(--panel) 70%)`,
          borderBottom: `1px solid ${color}30`,
          padding: 20,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `linear-gradient(${color}08 1px, transparent 1px), linear-gradient(90deg, ${color}08 1px, transparent 1px)`,
            backgroundSize: '32px 32px',
          }}
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close event details"
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 2,
            background: 'rgba(0,0,0,0.4)',
            border: 'none',
            borderRadius: 4,
            padding: 6,
            cursor: 'pointer',
            color: 'var(--text-muted)',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <X size={16} />
        </button>

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div
            style={{
              display: 'inline-block',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color,
              marginBottom: 8,
            }}
          >
            {label}
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>
            {event.title}
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* Time */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <CalendarClock size={16} style={{ color, flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
              {formatDateTime(event.startsAt)}
            </div>
            {event.endsAt && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                Ends {formatDateTime(event.endsAt)}
              </div>
            )}
          </div>
        </div>

        {/* Location — anchored to OSM place */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <MapPin size={16} style={{ color, flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
              {event.placeName || event.locationName}
            </div>
            {event.placeAddress && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                {event.placeAddress}
              </div>
            )}
            {!event.placeName && (
              <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
                Venue not yet on map
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        {event.description && (
          <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-secondary)' }}>
            {event.description}
          </div>
        )}

        {/* Trust / source */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 12px',
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid var(--border)',
            borderRadius: 6,
          }}
        >
          {verified ? (
            <ShieldCheck size={16} style={{ color: 'var(--green)', flexShrink: 0 }} />
          ) : (
            <Users size={16} style={{ color: 'var(--text-dim)', flexShrink: 0 }} />
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>
              {verified ? 'Verified source' : 'Community submission'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
              via {sourceLabel}
            </div>
          </div>
        </div>

        {/* CTA */}
        {event.sourceUrl && (
          <a
            href={event.sourceUrl}
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              padding: '12px 16px',
              background: color,
              color: '#0a0c10',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 700,
              textDecoration: 'none',
            }}
          >
            <ExternalLink size={15} />
            View on {sourceLabel}
          </a>
        )}
      </div>
    </div>
  );
}
